import {
	appliedFacetsForImpressionsAtom,
	selectedCategoryCodesAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { queueVendorImpression as queueVendorImpressionAction } from '@redux/vendorImpressions/thunks';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { STOREFRONT_CENTER_IMPRESSION_TYPE } from '../../client/pages/Storefront/components/McVrmLiteWidget/types';

type ImpressionDetailsWithCategory = VendorImpressions.ImpressionDetails & {
	category_id?: string | null;
};

const getCategoryCodesForImpression = (
	details: VendorImpressions.ImpressionDetails,
	categoryCodes: string[],
) => {
	if (details.impressionType === STOREFRONT_CENTER_IMPRESSION_TYPE) {
		return details.vendor.categories.map((category) => category.code);
	}

	if (details.impressionType !== 'MCVRM') {
		return categoryCodes;
	}

	const mcvrmCategoryId = (details as ImpressionDetailsWithCategory)
		?.category_id;

	if (mcvrmCategoryId == null) {
		return [];
	}

	const normalizedCategoryId = String(mcvrmCategoryId).trim();

	if (normalizedCategoryId.length === 0) {
		return [];
	}

	return normalizedCategoryId
		.split(',')
		.map((categoryCode) => categoryCode.trim())
		.filter(Boolean);
};

/**
 * Custom hook for queueing vendor impressions with automatic filter and
 * category tracking.
 *
 * Reads two Jotai atoms so components don't need to pass them explicitly:
 * - `appliedFacetsForImpressionsAtom` — current filter state for directory_filters
 * - `selectedCategoryCodesAtom` — selected category codes for category_id override
 *   (on multi-category pages these reflect the actual user selection rather than
 *   the Redux default which is always 'REC' on all-categories pages)
 *
 * @returns A callback function that queues a vendor impression
 *
 * @example
 * ```typescript
 * const queueVendorImpression = useQueueVendorImpression();
 *
 * const callback = () => {
 *   queueVendorImpression(impressionDetails, isSemanticSearch);
 * };
 * ```
 */
export const useQueueVendorImpression = () => {
	const dispatch = useDispatch();
	const appliedFilters = useAtomValue(appliedFacetsForImpressionsAtom);
	const categoryCodes = useAtomValue(selectedCategoryCodesAtom);

	const queueVendorImpression = useCallback(
		(
			details: VendorImpressions.ImpressionDetails,
			isSemanticSearchCalled?: boolean,
		) => {
			const categoryCodesForImpression = getCategoryCodesForImpression(
				details,
				categoryCodes,
			);

			return dispatch(
				queueVendorImpressionAction(
					details,
					isSemanticSearchCalled,
					appliedFilters,
					categoryCodesForImpression,
				),
			);
		},
		[dispatch, appliedFilters, categoryCodes],
	);

	return queueVendorImpression;
};
